import { useEffect, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Radio, FormControlLabel, FormControl, FormLabel, InputLabel, MenuItem, RadioGroup, Select, FormHelperText } from '@mui/material';
import { useEconomicActivitiesStore } from '@lib/store';
import { CountryEntity } from '@shared/api';
import { BankAccountInput } from './BankAccountInput';
import { SourcesOfWealth } from './sourceOfWealth';
import { Constants } from '@shared/constants';
import { Input } from '@shared/widgets';

const useStyles = makeStyles({
  hidden: {
    display: 'none',
  },
  uppercase: {
    'text-transform': 'uppercase',
  },
});

const CURRENCIES = ['CHF', 'EUR', 'USD'];
const ANNUAL_INCOMES = [
  { id: 50000, caption: 'Up to 250,000' },
  { id: 250000, caption: '250,000-500,000' },
  { id: 500000, caption: '500,000-750,000' },
  { id: 750000, caption: '750,000-1 million' },
  { id: 1000000, caption: '1 million+' },
];

interface Props {
  countries: CountryEntity[];
}

export const StepFinancialDetails = (props: Props) => {
  const classes = useStyles();

  const { data: economicActivities, getEconomicActivities } = useEconomicActivitiesStore();

  useEffect(() => {
    getEconomicActivities();
  }, []);

  // TODO: DIDN'T WORK BEFORE
  // const [bankAccountNumberError, setBankAccountNumberError] = useState<string | undefined>();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const usdCurrency = watch('usdCurrency');
  const eurCurrency = watch('eurCurrency');
  const chfCurrency = watch('chfCurrency');

  const usedCurrencies = useMemo(() => {
    const result: string[] = [];
    if (usdCurrency) result.push('USD');
    if (eurCurrency) result.push('EUR');
    if (chfCurrency) result.push('CHF');
    return result;
  }, [usdCurrency, eurCurrency, chfCurrency]);

  // TODO: DIDN'T WORK BEFORE
  // const checkBankAccountNumberIsValid = (value: string) => {
  //   const matchResult = value?.match(/[a-zA-Z0-9]{6}\.\d\d\.\d\d\.[a-zA-Z]{3}/gim);
  //   return !!value && !!matchResult;
  // };
  // const getCurrencyError = () => {
  //   let result = true;
  //   if (eurCurrency) {
  //     if (!getValues('bankAccountNumber').endsWith('EUR') && !getValues('bankAccountNumber2').endsWith('EUR')) {
  //       result = false;
  //     }
  //   } else if (usdCurrency) {
  //     if (!getValues('bankAccountNumber').endsWith('USD') && !getValues('bankAccountNumber2').endsWith('USD')) {
  //       result = false;
  //     }
  //   } else if (chfCurrency) {
  //     if (!getValues('bankAccountNumber').endsWith('CHF') && !getValues('bankAccountNumber2').endsWith('CHF')) {
  //       result = false;
  //     }
  //   }
  //   if (!result) return 'Bank Account currency does not match product currency';
  //   return undefined;
  // };
  // const validateBankAccountNumber = (value: string) => {
  //   if (checkBankAccountNumberIsValid(value)) {
  //     setBankAccountNumberError(getCurrencyError);
  //   } else {
  //     setBankAccountNumberError('Invalid Bank Account');
  //   }
  // };

  return (
    <Grid container>
      <Grid item xs={2}>
        <FormControl margin='normal' fullWidth error={!!errors.annualIncomeCurrency}>
          <InputLabel>Currency *</InputLabel>
          <Controller
            name='annualIncomeCurrency'
            control={control}
            rules={{ required: 'Currency is required' }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select {...field} value={field.value ?? ''} variant='standard' fullWidth>
                  {CURRENCIES.map((item) => (
                    <MenuItem key={item} value={item.toLowerCase()}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <FormControl margin='normal' fullWidth error={!!errors.annualIncomeAmount}>
          <InputLabel>Annual Income *</InputLabel>
          <Controller
            name='annualIncomeAmount'
            control={control}
            rules={{ required: 'Annual Income is required' }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select {...field} value={field.value ?? ''} variant='standard' fullWidth>
                  {ANNUAL_INCOMES.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.caption}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <FormControl margin='normal' fullWidth>
          <InputLabel>Currency</InputLabel>
          <Controller
            name='otherIncomeCurrency'
            control={control}
            render={({ field }) => (
              <Select {...field} value={field.value ?? ''} variant='standard' fullWidth>
                {CURRENCIES.map((item) => (
                  <MenuItem key={item} value={item.toLowerCase()}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='otherIncomeAmount'
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type='number'
              label='Other Income Amount (I.e. company proceeds, investments, rental income.)'
              margin='normal'
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
              }}
            />
          )}
        />
      </Grid>
      <SourcesOfWealth name='sow' caption='Source of Wealth' countries={props.countries} economicActivities={economicActivities} />
      <SourcesOfWealth name='sof' caption='Source of Funds' countries={props.countries} economicActivities={economicActivities} />
      <Grid item xs={4}>
        <FormControl margin='normal' fullWidth error={!!errors.netWorth}>
          <InputLabel>Net Worth *</InputLabel>
          <Controller
            name='netWorth'
            control={control}
            rules={{ required: 'Net Worth is required' }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select {...field} value={field.value ?? ''} variant='standard' fullWidth>
                  {Object.keys(Constants.NET_WORTHS).map((key) => (
                    <MenuItem key={key} value={key}>
                      {Constants.NET_WORTHS[key as keyof typeof Constants.NET_WORTHS]}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='bankAccountNumber'
          control={control}
          rules={{
            required: 'Bank Account Number is required',
            // validate: { currencyMatch: getCurrencyError }
          }}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <BankAccountInput
              // {...field}
              currencies={usedCurrencies}
              label='Bank Account Number'
              fullWidth
              required
              error={!!error}
              helperText={error?.message}
              // TODO: DIDN'T WORK BEFORE
              // error={!!bankAccountNumberError}
              // helperText={bankAccountNumberError}
              inputProps={{ className: classes.uppercase }}
              value={value}
              onBlur={() => {
                onBlur();
                // TODO: DIDN'T WORK BEFORE
                // validateBankAccountNumber(field.value);
              }}
              onChange={(e) => {
                // TODO: DIDN'T WORK BEFORE
                // setBankAccountNumberError(undefined);
                onChange(e.target.value + (usedCurrencies[0] || ''));
                setValue('bankAccountNumber2', usedCurrencies[1] ? e.target.value + usedCurrencies[1] : '');
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl margin='normal' fullWidth error={!!errors.anticipatedMonthlySpend}>
          <FormLabel component='label'>Anticipated Monthly Spend *</FormLabel>
          <RadioGroup row>
            {Object.keys(Constants.ANTICIPATED_MONTHLY_SPENDS).map((key, idx) => (
              <FormControlLabel
                key={idx}
                label={Constants.ANTICIPATED_MONTHLY_SPENDS[key as keyof typeof Constants.ANTICIPATED_MONTHLY_SPENDS]}
                labelPlacement='end'
                control={
                  <Controller
                    name='anticipatedMonthlySpend'
                    control={control}
                    rules={{ required: 'Anticipated Monthly Spend is required' }}
                    render={({ field }) => <Radio {...field} color='primary' value={key} checked={key === field.value} />}
                  />
                }
              />
            ))}
          </RadioGroup>
          {errors.anticipatedMonthlySpend?.message && <FormHelperText>{errors.anticipatedMonthlySpend?.message as string}</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );
};
